import type { Row } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { useLinkProps, useNavigate } from "@tanstack/react-router";
import { TableRow } from "@/components/ui/table";

export type DataTableRowLink = Omit<Parameters<typeof useLinkProps>[0], "children" | "params" | "search"> & {
  params?: Record<string, string | number>;
  search?: Record<string, unknown>;
};

const interactiveElementSelector = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  "summary",
  "[role='button']",
  "[role='checkbox']",
  "[data-row-link-ignore='true']",
].join(", ");

function shouldIgnoreRowActivation(target: EventTarget | null, currentTarget?: HTMLElement): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const interactiveElement = target.closest(interactiveElementSelector);

  return !!interactiveElement && interactiveElement !== currentTarget;
}

function isRowActivationKey(key: string): boolean {
  return key === "Enter" || key === " ";
}

type DataTableBaseRowProps<TData> = {
  row: Row<TData>;
  className: string;
  children: ReactNode;
};

type DataTableClickableRowProps<TData> = DataTableBaseRowProps<TData> & {
  onRowClick: (row: TData) => void;
};

type DataTableLinkRowProps<TData> = DataTableBaseRowProps<TData> & {
  rowLink: DataTableRowLink;
};

type DataTableRowLinkHandlers<TElement extends HTMLElement> = Pick<
  React.HTMLAttributes<TElement>,
  "onClick" | "onKeyDown" | "onMouseEnter" | "onFocus" | "onTouchStart"
>;

function createClickableRowKeyDownHandler<TElement extends HTMLElement, TData>(
  row: Row<TData>,
  onRowClick: (row: TData) => void,
): React.KeyboardEventHandler<TElement> {
  return function handleClickableRowKeyDown(event) {
    if (shouldIgnoreRowActivation(event.target, event.currentTarget)) {
      return;
    }

    if (!isRowActivationKey(event.key)) {
      return;
    }

    event.preventDefault();
    onRowClick(row.original);
  };
}

function createClickableRowClickHandler<TElement extends HTMLElement, TData>(
  row: Row<TData>,
  onRowClick: (row: TData) => void,
): React.MouseEventHandler<TElement> {
  return (event) => {
    if (shouldIgnoreRowActivation(event.target, event.currentTarget)) {
      return;
    }

    onRowClick(row.original);
  };
}

function useDataTableRowLinkHandlers<TElement extends HTMLElement>(
  rowLink: DataTableRowLink,
): DataTableRowLinkHandlers<TElement> {
  const navigate = useNavigate();
  const linkOptions = {
    preload: "intent" as const,
    ...rowLink,
  };
  const { href: _, target: __, rel: ___, ...linkProps } = useLinkProps(linkOptions as never);
  const { preload: ____, disabled: _____, ...navigateOptions } = linkOptions;

  const handleClick: React.MouseEventHandler<TElement> = (event) => {
    if (shouldIgnoreRowActivation(event.target, event.currentTarget)) {
      return;
    }

    linkProps.onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
  };

  const handleKeyDown: React.KeyboardEventHandler<TElement> = (event) => {
    if (shouldIgnoreRowActivation(event.target, event.currentTarget)) {
      return;
    }

    if (!isRowActivationKey(event.key)) {
      return;
    }

    event.preventDefault();
    navigate(navigateOptions as never);
  };

  const handleMouseEnter: React.MouseEventHandler<TElement> = (event) => {
    linkProps.onMouseEnter?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
  };

  const handleFocus: React.FocusEventHandler<TElement> = (event) => {
    linkProps.onFocus?.(event as unknown as React.FocusEvent<HTMLAnchorElement>);
  };

  const handleTouchStart: React.TouchEventHandler<TElement> = (event) => {
    linkProps.onTouchStart?.(event as unknown as React.TouchEvent<HTMLAnchorElement>);
  };

  return {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onMouseEnter: handleMouseEnter,
    onFocus: handleFocus,
    onTouchStart: handleTouchStart,
  };
}

export function DataTableCardStaticRow<TData>({
  row,
  className,
  children,
}: DataTableBaseRowProps<TData>): React.JSX.Element {
  return (
    <div data-state={row.getIsSelected() && "selected"} className={className}>
      {children}
    </div>
  );
}

export function DataTableCardClickableRow<TData>({
  row,
  onRowClick,
  className,
  children,
}: DataTableClickableRowProps<TData>): React.JSX.Element {
  const handleDataTableCardClick = createClickableRowClickHandler<HTMLDivElement, TData>(row, onRowClick);
  const handleKeyDown = createClickableRowKeyDownHandler<HTMLDivElement, TData>(row, onRowClick);

  return (
    <div
      data-state={row.getIsSelected() && "selected"}
      className={className}
      onClick={handleDataTableCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}

export function DataTableCardLinkRow<TData>({
  row,
  rowLink,
  className,
  children,
}: DataTableLinkRowProps<TData>): React.JSX.Element {
  const linkHandlers = useDataTableRowLinkHandlers<HTMLDivElement>(rowLink);

  return (
    <div
      data-state={row.getIsSelected() && "selected"}
      className={className}
      role="link"
      tabIndex={0}
      {...linkHandlers}
    >
      {children}
    </div>
  );
}

export function DataTableTableStaticRow<TData>({
  row,
  className,
  children,
}: DataTableBaseRowProps<TData>): React.JSX.Element {
  return (
    <TableRow data-state={row.getIsSelected() && "selected"} className={className}>
      {children}
    </TableRow>
  );
}

export function DataTableTableClickableRow<TData>({
  row,
  onRowClick,
  className,
  children,
}: DataTableClickableRowProps<TData>): React.JSX.Element {
  const handleDataTableRowClick = createClickableRowClickHandler<HTMLTableRowElement, TData>(row, onRowClick);
  const handleKeyDown = createClickableRowKeyDownHandler<HTMLTableRowElement, TData>(row, onRowClick);

  return (
    <TableRow
      data-state={row.getIsSelected() && "selected"}
      className={className}
      onClick={handleDataTableRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {children}
    </TableRow>
  );
}

export function DataTableTableLinkRow<TData>({
  row,
  rowLink,
  className,
  children,
}: DataTableLinkRowProps<TData>): React.JSX.Element {
  const linkHandlers = useDataTableRowLinkHandlers<HTMLTableRowElement>(rowLink);

  return (
    <TableRow
      data-state={row.getIsSelected() && "selected"}
      className={className}
      role="link"
      tabIndex={0}
      {...linkHandlers}
    >
      {children}
    </TableRow>
  );
}
